import {useQuery} from '@apollo/client';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
    GET_COMPLAINT_STATUS_LIST,
    GetComplaintStatusListResponse,
} from '../graphql/queries/getComplaintStatusList';
import {pickLocalizedText} from '../../../../../utils/i18nText';

/**
 * 客诉状态字典 hook。
 *
 * 拉取后端 getComplaintStatusList 一次，在内存缓存为 code → 当前 locale 文案的 Map；
 * 调用方通过返回的 getStatusText(code) 渲染。未命中时退回 code 本身，避免空白。
 *
 * Apollo cache-first：同一会话内只发一次请求；切换语言时 useMemo 重算映射但不重新请求。
 */
export function useComplaintStatusText(): {
    getStatusText: (code: string | undefined | null) => string;
    loading: boolean;
} {
    const {i18n} = useTranslation();
    const lang = i18n.language;

    const {data, loading} = useQuery<GetComplaintStatusListResponse>(GET_COMPLAINT_STATUS_LIST, {
        fetchPolicy: 'cache-first',
    });

    const textMap = useMemo(() => {
        const map = new Map<string, string>();
        (data?.getComplaintStatusList ?? []).forEach((opt) => {
            map.set(opt.code, pickLocalizedText(opt.texts, lang));
        });
        return map;
    }, [data, lang]);

    const getStatusText = (code: string | undefined | null): string => {
        if (!code) return '';
        return textMap.get(code) ?? code;
    };

    return {getStatusText, loading};
}
